import { useToast } from '@chakra-ui/react';
import React, { createContext, useState, useContext } from 'react';
import { container } from 'tsyringe';
import { DiskusiApiRepository } from '../../../../repository/api/DiskusiApiRepository';
import { Diskusi } from '../../../../../entities/Diskusi';

interface IState {
  state: {
    diskusiList: Diskusi[];
    message: string;
    isLoading: boolean;
    isSubmitting: boolean;
    page: number;
    totalPage: number;
    totalRow: number;
  };
  fetchDiskusi: (pengetahuanId: number) => Promise<void>;
  handleSubmit: (pengetahuanId: number) => Promise<void>;
  setDiskusiState: (key: keyof IState['state'], value: any) => void;
}

const initialState: IState = {
  state: {
    diskusiList: [],
    message: '',
    isLoading: false,
    isSubmitting: false,
    page: 1,
    totalPage: 0,
    totalRow: 0,
  },
  fetchDiskusi: async () => {},
  handleSubmit: async () => {},
  setDiskusiState: () => {},
};

const Context = createContext<IState>(initialState);
const { Provider: DiskusiProvider } = Context;

const Provider: React.FC = ({ children }) => {
  const [state, setState] = useState<IState['state']>(initialState.state);
  const toast = useToast();
  const diskusiRepository = container.resolve(DiskusiApiRepository);

  const setDiskusiState = (key: keyof IState['state'], value: any) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const fetchDiskusi = async (pengetahuanId: number) => {
    try {
      setDiskusiState('isLoading', true);
      const data = await diskusiRepository.getDiskusi(pengetahuanId, state.page);
      setDiskusiState('diskusiList', data.data || []);
      setDiskusiState('totalPage', data.totalPage || 0);
      setDiskusiState('totalRow', data.totalRow || 0);
    } catch (error: any) {
      console.error('Error fetching diskusi:', error);
      setDiskusiState('diskusiList', []);
      
      // Only show toast if it's not a 404 (no data yet)
      if (error?.response?.status !== 404) {
        toast({
          title: 'Gagal memuat diskusi',
          description: error?.response?.data?.message || 'Terjadi kesalahan',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      setDiskusiState('isLoading', false);
    }
  };

  const handleSubmit = async (pengetahuanId: number) => {
    try {
      setDiskusiState('isSubmitting', true);
      await diskusiRepository.storeDiskusi(pengetahuanId, state.message);
      setDiskusiState('message', '');
      setDiskusiState('page', 1);
      toast({
        title: 'Diskusi berhasil dikirim',
        status: 'success',
        isClosable: true,
        position: 'top-right',
      });
      await fetchDiskusi(pengetahuanId);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || 'Gagal mengirim diskusi';
      toast({
        title: 'Gagal mengirim diskusi',
        description: errorMessage,
        status: 'error',
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setDiskusiState('isSubmitting', false);
    }
  };

  return (
    <DiskusiProvider
      value={{
        state,
        fetchDiskusi,
        handleSubmit,
        setDiskusiState,
      }}
    >
      {children}
    </DiskusiProvider>
  );
};

export const useDiskusiContext = () => useContext(Context);

const DiskusiContext = {
  useDiskusiContext,
  Provider,
};

export default DiskusiContext;
